import { useMemo, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Flex, Text } from "@radix-ui/themes";
import { motion, useInView } from "motion/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import SocialAuthButtons from "@/components/auth/forms/SocialAuthButtons";
import { useAuthControllerCheckEmail } from "@/lib/lutely-api/gen/auth/auth";
import { initiateOtcAuth } from "@/lib/auth";
import {
  getLastAuth,
  setAuthHandoff,
  saveOtcRequestTimestamp,
} from "@/lib/storage-utils";

const emailSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email." }),
});

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const },
  },
};

/**
 * Same auth modal and post-auth routing as the top nav's Sign up (LUT-983):
 * no forced redirect, so new users get the persona picker.
 */
export default function JoinLutelySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const navigate = useNavigate();
  const { mutateAsync: checkEmail } = useAuthControllerCheckEmail();

  const lastAuth = useMemo(() => getLastAuth(), []);
  const prefillEmail =
    lastAuth?.email &&
    !lastAuth.email.includes("privaterelay.appleid.com") &&
    lastAuth.method !== "google" &&
    lastAuth.method !== "apple"
      ? lastAuth.email
      : "";

  const form = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: prefillEmail },
  });

  const onSubmit = async (data: z.infer<typeof emailSchema>) => {
    try {
      const { exists, authMethods } = await checkEmail({
        data: { email: data.email },
      });

      if (!exists) {
        // New user → password signup in modal
        setAuthHandoff({ email: data.email, initialPhase: "signup" });
        navigate({
          to: ".",
          search: { auth: "signup" },
          replace: true,
          resetScroll: false,
        });
      } else {
        // Existing user → OTC code in modal (code-first for landing page)
        await initiateOtcAuth(data.email);
        saveOtcRequestTimestamp(data.email);
        setAuthHandoff({ email: data.email, authMethods });
        navigate({
          to: ".",
          search: { auth: "verify-code" },
          replace: true,
          resetScroll: false,
        });
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      {/* Background avatar arc image — hidden on mobile, matches Figma positioning */}
      <div
        className="pointer-events-none absolute left-0 right-0 top-[-20px] hidden h-[1122px] px-10 md:block"
        aria-hidden="true"
      >
        <div className="relative aspect-[3456/3509] w-full">
          <img
            src="/images/featured/signup-background.png"
            alt=""
            className="absolute inset-0 size-full object-cover"
          />
        </div>
      </div>

      <ContainerWrapper maxWidth="1400px">
        <div className="relative z-10 pt-20 pb-20 md:pt-[200px] md:pb-[200px] lg:pt-[280px] lg:pb-[260px]">
          <div className="mx-auto flex max-w-[700px] flex-col items-center">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <Flex
                direction="column"
                className="items-center gap-8 md:gap-[60px]"
              >
                {/* Heading */}
                <motion.div variants={fadeUp}>
                  <Flex direction="column" className="items-center text-center">
                    <h2 className="text-center font-figtree text-[46px] font-extrabold leading-[1] tracking-[-0.72px] text-blue-900 md:text-[54px] lg:text-[72px]">
                      Be part of the
                      <br />
                      community where
                    </h2>
                    <p className="mt-0 text-center font-permanent-marker text-[38px] leading-[140%] text-blue-600 md:text-[42px] lg:text-[54px]">
                      artists are first.
                    </p>
                  </Flex>
                </motion.div>

                {/* Signup card */}
                <motion.div variants={fadeUp} className="w-full max-w-[480px]">
                  <div className="w-full rounded-[20px] bg-white p-2 shadow-[0_14px_20px_-1px_rgba(5,12,43,0.14)] md:p-4">
                    <Flex
                      direction="column"
                      className="w-full max-w-full items-center rounded-lg border border-blue-200 bg-white px-4 pb-4 pt-6 md:px-10 md:pb-5 md:pt-10 gap-6 md:gap-10"
                    >
                      {/* Gradient title */}
                      <Text
                        className="text-center text-[20px] font-semibold font-open-sans"
                        style={{
                          background:
                            "linear-gradient(90deg, #5C10D6 17%, #E01D57 50%, #3042E2 82%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        Join Lutely and get started.
                      </Text>

                      {/* Auth content */}
                      <Flex
                        direction="column"
                        className="w-full items-center gap-6"
                      >
                        {/* Email signup */}
                        <form
                          onSubmit={form.handleSubmit(onSubmit)}
                          className="flex w-full flex-col gap-4"
                          noValidate
                        >
                          <input
                            {...form.register("email")}
                            type="email"
                            placeholder="Email"
                            autoComplete="email"
                            data-testid="join-email-input"
                            className="w-full h-11 rounded border border-[#acb3bd] px-4 text-md text-black placeholder:text-gray-500 focus:border-blue-600 focus:outline-none"
                          />
                          {form.formState.errors.email && (
                            <Text size="1" color="red">
                              {form.formState.errors.email.message}
                            </Text>
                          )}
                          <Button
                            type="submit"
                            loading={form.formState.isSubmitting}
                            radius="full"
                            data-testid="join-signup-button"
                            className="h-12 text-[16px] font-bold"
                          >
                            Sign Up with Email
                          </Button>
                        </form>

                        {/* Social auth — light variant */}
                        <SocialAuthButtons
                          variant="landing"
                          labelPrefix="Sign Up"
                        />
                      </Flex>

                      {/* Sign in footer */}
                      <Flex className="items-center gap-2">
                        <Text className="text-md font-open-sans text-gray-8">
                          Already have an account?
                        </Text>
                        <Link
                          to="."
                          search={{ auth: "login" as const }}
                          className="text-md font-bold text-blue-900 transition-colors hover:text-blue-600"
                        >
                          Sign In
                        </Link>
                      </Flex>
                    </Flex>
                  </div>
                </motion.div>
              </Flex>
            </motion.div>
          </div>
        </div>
      </ContainerWrapper>
    </section>
  );
}
